import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";

const MAX_ENTITY_NAME_LENGTH = 25;
const ORG_NAME_MAX_LENGTH = 50;
// Text that starts with a letter, digit or space, followed by letters, digits, spaces, _ and -. Must have at least one character.
// (Accepts: "Müller", "Anne-Marie", "Jean_Luc", "Straße 2"   Rejects: "O'Brien", "Name@1", "_Anna", "")
export const ERROR_VALIDATION_PATTERN = /^(?:[\p{L}\p{M}\p{N}\s]+)(?:[\p{L}\p{M}\p{N}_\-\s]*)$/u;

interface FormFieldValidation {
    validator: ValidatorFn;
    errorKey: string;
    message: () => string;
    maxLength?: number;
}

export const formFieldTypeValidations: Record<string, FormFieldValidation> = {
    ENTITY_NAME_MAX_LENGTH_VALIDATION: {
        validator: Validators.maxLength(MAX_ENTITY_NAME_LENGTH),
        errorKey: 'maxlength',
        message: () => `Must be less than ${MAX_ENTITY_NAME_LENGTH} characters`,
        maxLength: MAX_ENTITY_NAME_LENGTH
    },
    ORG_NAME_MAX_LENGTH_VALIDATION: {
        validator: Validators.maxLength(ORG_NAME_MAX_LENGTH),
        errorKey: 'maxlength',
        message: () => `Must be less than ${ORG_NAME_MAX_LENGTH} characters`,
        maxLength: ORG_NAME_MAX_LENGTH
    },
    ENTITY_NAME_SPECIAL_CHARACTERS_VALIDATION: {
        validator: Validators.pattern(ERROR_VALIDATION_PATTERN),
        errorKey: 'pattern',
        message: () => `Only letters (including international characters), numbers, spaces, underscores (_), and hyphens (-) are allowed`,
    },
    EMAIL_VALIDATION: {
        validator: Validators.email,
        errorKey: 'email',
        message: () => `Invalid Email ID`,
    },
};

export const formGroupValidations = {
    ZoneDependent: 'ZONE_DEPENDENT_VALIDATION',
    ZoneDuplicate: 'ZONE_DUPLICATE_VALIDATION'
}

export function zoneDependentValidator(metaData: any): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
        if (!(formGroup instanceof FormGroup)) return null;

        const requiredFieldControl = formGroup.get(metaData.requiredFieldId);
        if (!requiredFieldControl) return null;

        const requiredFieldValue = requiredFieldControl.value?.trim();

        // Check if any of the dependent fields (zones) are filled
        const anyDependentFieldFilled = metaData.dependentFieldIds.some((zoneFieldId: string) =>
            !!formGroup.get(zoneFieldId)?.value?.trim()
        );

        // Apply validation: If any zone is filled, the required field must be filled
        if (!requiredFieldValue && anyDependentFieldFilled) {
            // Set error message dynamically based on the metaData
            requiredFieldControl.setErrors({
                [metaData.errorName]: metaData.errorMessage,
            });
        } else {
            // Remove the error if the condition is no longer met
            const existingErrors = requiredFieldControl.errors || {};
            if (existingErrors[metaData.errorName]) {
                delete existingErrors[metaData.errorName];
                // If there are no other errors, set the control errors to null
                requiredFieldControl.setErrors(Object.keys(existingErrors).length ? existingErrors : null);
            }
        }
        return null;
    };
}

export function noDuplicateZoneNamesValidator(metaData: any): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
        if (!(formGroup instanceof FormGroup)) return null;

        const controls = formGroup.controls;
        const zoneControls = metaData.fieldIds.map((id: string) => ({ key: id, control: controls[id] }));
        const valueMap = new Map<string, string[]>();

        // Track non-empty values and where they appear
        zoneControls.forEach(({ key, control }: { key: string; control: AbstractControl }) => {
            const value = control.value?.trim();
            if (value) {
                const lower = value.toLowerCase();
                if (!valueMap.has(lower)) valueMap.set(lower, []);
                valueMap.get(lower)!.push(key);
            }
        });

        // Reset existing duplicate errors
        zoneControls.forEach(({ control }: { control: AbstractControl }) => {
            const errors = control.errors;
            if (errors?.[metaData.errorName]) {
                delete errors[metaData.errorName];
                control.setErrors(Object.keys(errors).length ? errors : null);
            }
        });

        // Apply new duplicateZone errors
        for (const [_, keys] of valueMap.entries()) {
            if (keys.length > 1) {
                keys.forEach(dupKey => {
                    const control = formGroup.get(dupKey);
                    if (control) {
                        const currentErrors = control.errors || {};
                        currentErrors[metaData.errorName] = metaData.errorMessage; // Set the dynamic error message
                        control.setErrors(currentErrors);
                    }
                });
            }
        }
        return null;
    };
}